import {
  AuthState,
  Image,
  LoadBalancerType,
  Me,
  Passkey,
  PasskeyOptions,
  PriceList,
  Project,
  RecoveryCodes,
  Region,
  ServerType,
  SshKey,
  TotpSetup,
  type BootstrapRequest,
  type CreateProjectRequest,
  type Locale,
} from "@cloudserver/contracts";
import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "./client.js";

export const keys = {
  auth: ["auth"] as const,
  passkeys: ["passkeys"] as const,
  projects: ["projects"] as const,
  project: (id: string) => ["projects", id] as const,
  catalogue: (id: string, kind: string) => ["projects", id, "catalogue", kind] as const,
};

export const useAuthState = () =>
  useQuery({ queryKey: keys.auth, queryFn: () => api("/auth/state", { schema: AuthState }) });

/**
 * The language switcher of a signed-in person: their choice of the panel's
 * language, kept with the account so every browser of theirs follows it
 * (docs/users.md, "Language").
 */
export function useSetLocale() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (locale: Locale) =>
      api("/me/locale", { method: "PUT", body: { locale }, schema: Me }),
    onSuccess: (me) =>
      client.setQueryData<AuthState>(keys.auth, (old) => (old ? { ...old, me } : old)),
  });
}

/** After any sign-in step the auth state is reloaded from the server. */
function useAuthMutation<T, R>(fn: (input: T) => Promise<R>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.auth }),
  });
}

export const useBootstrap = () =>
  useAuthMutation((body: BootstrapRequest) =>
    api("/auth/bootstrap", { method: "POST", body, schema: Me }),
  );

export const usePasswordSignIn = () =>
  useAuthMutation((body: { email: string; password: string }) =>
    api("/auth/password", { method: "POST", body, schema: AuthState }),
  );

export const useCodeSignIn = () =>
  useAuthMutation(({ code, kind }: { code: string; kind: "totp" | "recovery" }) =>
    api(`/auth/${kind}`, { method: "POST", body: { code }, schema: Me }),
  );

export const usePasskeySignIn = () =>
  useAuthMutation(async () => {
    const { challengeId, options } = await api("/auth/passkey/options", {
      method: "POST",
      schema: PasskeyOptions,
    });
    const response = await startAuthentication({ optionsJSON: options as never });
    return api("/auth/passkey", { method: "POST", body: { challengeId, response }, schema: Me });
  });

export const useSignOut = () => useAuthMutation(() => api("/auth/logout", { method: "POST" }));

export const usePasskeys = () =>
  useQuery({
    queryKey: keys.passkeys,
    queryFn: () => api("/me/passkeys", { schema: z.array(Passkey) }),
  });

export function useAddPasskey() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => {
      const { challengeId, options } = await api("/me/passkeys/options", {
        method: "POST",
        schema: PasskeyOptions,
      });
      const response = await startRegistration({ optionsJSON: options as never });
      return api("/me/passkeys", {
        method: "POST",
        body: { challengeId, name, response },
        schema: Passkey,
      });
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: keys.passkeys });
      await client.invalidateQueries({ queryKey: keys.auth });
    },
  });
}

export function useRemovePasskey() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/me/passkeys/${encodeURIComponent(id)}`, { method: "DELETE" }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: keys.passkeys });
      await client.invalidateQueries({ queryKey: keys.auth });
    },
  });
}

export const useTotpSetup = () =>
  useMutation({ mutationFn: () => api("/me/totp/setup", { method: "POST", schema: TotpSetup }) });

export const useTotpEnable = () =>
  useAuthMutation((code: string) =>
    api("/me/totp/enable", { method: "POST", body: { code }, schema: RecoveryCodes }),
  );

export const useTotpDisable = () => useAuthMutation(() => api("/me/totp", { method: "DELETE" }));

export const useProjects = (enabled = true) =>
  useQuery({
    queryKey: keys.projects,
    queryFn: () => api("/projects", { schema: z.array(Project) }),
    enabled,
  });

export const useProject = (id: string) =>
  useQuery({
    queryKey: keys.project(id),
    queryFn: () => api(`/projects/${id}`, { schema: Project }),
  });

export function useCreateProject() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateProjectRequest) =>
      api("/projects", { method: "POST", body, schema: Project }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.projects }),
  });
}

/** Project writes refresh every project query (list, detail, catalogue). */
function useProjectMutation<T, R>(fn: (input: T) => Promise<R>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.projects }),
  });
}

/** Takes the project id when called, so one hook serves every row of a list. */
export const useRefreshCatalogue = () =>
  useProjectMutation((id: string) =>
    api(`/projects/${id}/catalogue/refresh`, { method: "POST", schema: Project }),
  );

export const useReplaceToken = (id: string) =>
  useProjectMutation((credential: string) =>
    api(`/projects/${id}/connection`, { method: "PUT", body: { credential }, schema: Project }),
  );

export const useRenameProject = (id: string) =>
  useProjectMutation((name: string) =>
    api(`/projects/${id}`, { method: "PATCH", body: { name }, schema: Project }),
  );

/**
 * Only the project list is refreshed: refetching the removed project answers
 * 404, the open page turns into an error and unmounts the dialog before its
 * success callback (the way back to the project list) can run. Its cached
 * data is collected once nothing shows it.
 */
export const useForgetProject = (id: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api(`/projects/${id}`, { method: "DELETE" }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.projects, exact: true }),
  });
};

const CATALOGUE = {
  regions: z.array(Region),
  "server-types": z.array(ServerType),
  images: z.array(Image),
  prices: PriceList,
  "load-balancer-types": z.array(LoadBalancerType),
} as const;
export type CatalogueKind = keyof typeof CATALOGUE;
type CatalogueData = { [K in CatalogueKind]: z.infer<(typeof CATALOGUE)[K]> };

export const useCatalogue = <K extends CatalogueKind>(id: string, kind: K) =>
  useQuery({
    queryKey: keys.catalogue(id, kind),
    // The schema is picked by `kind`, so the parsed value has that kind's type.
    queryFn: async (): Promise<CatalogueData[K]> =>
      (await api(`/projects/${id}/catalogue/${kind}`, {
        schema: CATALOGUE[kind] as z.ZodType,
      })) as CatalogueData[K],
  });

export const useSshKeys = (projectId: string) =>
  useQuery({
    queryKey: ["projects", projectId, "ssh-keys"],
    queryFn: () => api(`/projects/${projectId}/ssh-keys`, { schema: z.array(SshKey) }),
  });

export const useCreateSshKey = (projectId: string) =>
  useProjectMutation((body: { name: string; publicKey: string }) =>
    api(`/projects/${projectId}/ssh-keys`, {
      method: "POST",
      body,
      schema: SshKey,
      idempotencyKey: crypto.randomUUID(),
    }),
  );

export const useRenameSshKey = (projectId: string) =>
  useProjectMutation(({ id, name }: { id: string; name: string }) =>
    api(`/projects/${projectId}/ssh-keys/${id}`, {
      method: "PATCH",
      body: { name },
      schema: SshKey,
    }),
  );

export const useDeleteSshKey = (projectId: string) =>
  useProjectMutation((id: string) =>
    api(`/projects/${projectId}/ssh-keys/${id}`, { method: "DELETE" }),
  );
